import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LanguageToggle from './LanguageToggle';
import { useLanguage } from '../context/LanguageContext';

const DEFAULT_NAV = [
  { label: 'Home', target: 'hero' },
  { label: 'About', target: 'about' },
  { label: 'Services', target: 'services' },
  { label: 'Portfolio', target: 'portfolio' },
  { label: 'Contact Us', target: 'contact' },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [navItems, setNavItems] = useState(DEFAULT_NAV);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { getField } = useLanguage();

  // Load menu from DB
  useEffect(() => {
    fetch('/api/menu')
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data?.length > 0) {
          // Build tree from flat list with parent
          const all = d.data;
          const topLevel = all.filter(m => !m.parent);
          const tree = topLevel.map(m => ({
            label: m.label,
            target: m.target,
            type: m.type,
            children: all.filter(c => c.parent && c.parent.toString() === m._id?.toString()).map(c => ({
              label: c.label,
              target: c.target,
              type: c.type,
            })),
          }));
          setNavItems(tree);
        }
      })
      .catch(() => {});
  }, []);

  // Handle scroll state
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const sections = navItems.map(item => item.target);
      let current = sections[0];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) {
            current = id;
          }
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNavClick = useCallback((item) => {
    setMobileOpen(false);
    setOpenDropdown(null);
    const target = item.target || '';

    if (target.startsWith('/')) {
      navigate(target);
      return;
    }
    if (target.startsWith('http')) {
      window.open(target, '_blank');
      return;
    }

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(target);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return;
    }

    const el = document.getElementById(target);
    if (el) {
      const offset = 72;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, [location.pathname, navigate]);

  const dropdownStyles = {
    container: {
      position: 'relative',
      display: 'inline-flex',
    },
    menu: {
      position: 'absolute',
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      paddingTop: 8,
      zIndex: 100,
    },
    menuInner: {
      background: 'rgba(20, 20, 30, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.1)',
      padding: '8px 0',
      minWidth: 180,
      boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
    },
    item: {
      display: 'block',
      width: '100%',
      padding: '10px 20px',
      background: 'none',
      border: 'none',
      color: '#ccc',
      fontSize: 14,
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap',
    },
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
      <div className="navbar__inner">
        {/* Logo */}
        <a
          href="/"
          className="navbar__logo"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick({ target: 'hero' });
          }}
        >
          <img
            src="/logo-black-h.png"
            alt="KBY Interactive"
            style={{ height: '45px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
          />
        </a>

        {/* Desktop links */}
        <div className="navbar__links">
          {navItems.map((item, idx) => {
            const hasChildren = item.children && item.children.length > 0;

            if (!hasChildren) {
              return (
                <button
                  key={item.target + idx}
                  className={`navbar__link ${activeSection === item.target ? 'navbar__link--active' : ''}`}
                  onClick={() => handleNavClick(item)}
                >
                  {getField(item, 'label')}
                </button>
              );
            }

            // Dropdown menu
            return (
              <div
                key={item.target + idx}
                style={dropdownStyles.container}
                onMouseEnter={() => setOpenDropdown(idx)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className={`navbar__link ${activeSection === item.target ? 'navbar__link--active' : ''}`}
                  onClick={() => item.target ? handleNavClick(item) : setOpenDropdown(openDropdown === idx ? null : idx)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {getField(item, 'label')}
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor" style={{ marginLeft: 2, transition: 'transform 0.2s', transform: openDropdown === idx ? 'rotate(180deg)' : '' }}>
                    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  </svg>
                </button>
                {openDropdown === idx && (
                  <div style={dropdownStyles.menu}>
                    <div style={dropdownStyles.menuInner}>
                      {item.children.map((child, ci) => (
                        <button
                          key={ci}
                          style={dropdownStyles.item}
                          onClick={() => handleNavClick(child)}
                          onMouseEnter={e => { e.target.style.background = 'rgba(255,255,255,0.08)'; e.target.style.color = 'var(--color-primary)'; }}
                          onMouseLeave={e => { e.target.style.background = 'none'; e.target.style.color = '#ccc'; }}
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Language Toggle */}
        <LanguageToggle />

        {/* Hamburger */}
        <button
          className={`navbar__hamburger ${mobileOpen ? 'navbar__hamburger--open' : ''}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`navbar__mobile ${mobileOpen ? 'navbar__mobile--open' : ''}`}>
        {navItems.map((item, idx) => (
          <div key={item.target + idx}>
            <button
              className={`navbar__link ${activeSection === item.target ? 'navbar__link--active' : ''}`}
              onClick={() => {
                if (item.children?.length > 0) {
                  setOpenDropdown(openDropdown === idx ? null : idx);
                } else {
                  handleNavClick(item);
                }
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}
            >
              {getField(item, 'label')}
              {item.children?.length > 0 && (
                <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor" style={{ marginLeft: 'auto', transition: 'transform 0.2s', transform: openDropdown === idx ? 'rotate(180deg)' : '' }}>
                  <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
              )}
            </button>
            {/* Mobile submenu */}
            {item.children?.length > 0 && openDropdown === idx && (
              <div style={{ paddingLeft: 20 }}>
                {item.children.map((child, ci) => (
                  <button key={ci} className="navbar__link" onClick={() => handleNavClick(child)} style={{ fontSize: 14, opacity: 0.8 }}>
                    {child.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
}

export default Navbar;
